import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map, tap, mergeMap } from 'rxjs/operators';
import { of as RxOf } from 'rxjs/observable/of';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { cloneDeep as _cloneDeep } from 'lodash';
import { IAccountGenericSetting } from '@blcloud/bl-datamodel';
import { AccountGenericSettingResource } from '@blcloud/bl-ng-resource';
import { BlApiErrorInfo, HttpStatus } from '@blcloud/bl-ng-common';
import { LoginInfoResourceService } from '@blcloud/bl-ng-share-module';
import {
  SCREEN_ACTION_SETTING_ID,
  IBizMenuPageScreenActionSetting,
  BaseScreenActionSetting,
  MacMenuCommonPageId,
} from './biz-menu-page-screen-action-setting-modal.define';

/**
 * 業務メニュー画面動作設定管理サービス
 */
@Injectable()
export class BizMenuPageScreenActionSettingService {
  /** アカウント別汎用設定 */
  private _accountGenericSetting: IAccountGenericSetting;

  /** 業務メニュー画面動作設定 */
  private _screenActionSetting: IBizMenuPageScreenActionSetting;
  /** 業務メニュー画面動作設定 */
  get screenActionSetting(): IBizMenuPageScreenActionSetting {
    return this._screenActionSetting;
  }

  /** 業務メニュー画面動作設定変更通知Subject */
  private changeSettingSubject = new BehaviorSubject<IBizMenuPageScreenActionSetting>(undefined);
  /** 業務メニュー画面動作設定変更通知ストリーム */
  get changeSetting$(): Observable<IBizMenuPageScreenActionSetting> {
    return this.changeSettingSubject.asObservable();
  }

  constructor(
    private loginInfoResourceService: LoginInfoResourceService,
    private accountGenericSettingResource: AccountGenericSettingResource,
  ) {
  }

  /**
   * 業務メニュー画面動作設定を取得します
   * @returns 業務メニュー画面動作設定
   */
  fetch(): Observable<IBizMenuPageScreenActionSetting> {
    return this.getAccountGenericSetting().pipe(
      tap(accountGenericSetting => {
        this._accountGenericSetting = accountGenericSetting;
      }),
      map(accountGenericSetting => {
        return this.parseScreenActionSetting(accountGenericSetting);
      }),
      tap(screenActionSetting => {
        this._screenActionSetting = screenActionSetting;
        this.changeSettingSubject.next(screenActionSetting);
      }),
    );
  }

  /**
   * 業務メニュー画面動作設定を保存します。
   * @param screenActionSetting 業務メニュー画面動作設定
   */
  save(screenActionSetting: IBizMenuPageScreenActionSetting): Observable<any> {
    if (this._accountGenericSetting) {
      // 更新
      const entity: IAccountGenericSetting = {
        blTenantId: this._accountGenericSetting.blTenantId,
        blUserId: this._accountGenericSetting.blUserId,
        pageId: this._accountGenericSetting.pageId,
        settingDataId: this._accountGenericSetting.settingDataId,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };

      return this.putAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );

    } else {
      // 新規作成
      const entity: IAccountGenericSetting = {
        pageId: MacMenuCommonPageId,
        settingDataId: SCREEN_ACTION_SETTING_ID,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };
      return this.postAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );
    }
  }

  /**
   * blUserId取得
   */
  private getBlUserId(): Observable<string> {
    return this.loginInfoResourceService.getLoginInfo().pipe(
      map(loginInfo => loginInfo.blUserId)
    );
  }

  /**
   * アカウント別汎用設定を取得します
   * @returns アカウント別汎用設定
   */
  private getAccountGenericSetting(): Observable<IAccountGenericSetting> {
    // 3業務（整備・鈑金・車販）を統合した画面のため、既存の採番ルールに該当するページIDが存在しない
    // そのため、画面識別用に独自のIDを定義する
    const pageId = MacMenuCommonPageId;
    const settingDataId = SCREEN_ACTION_SETTING_ID;

    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        const id = this.accountGenericSettingResource.getIdString({
          blUserId,
          pageId,
          settingDataId
        });
        return this.accountGenericSettingResource.getById(id);
      }),
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return RxOf(null);
        } else {
          throw error;
        }
      })
    );
  }

  /**
   * アカウント別汎用設定を更新します。
   * @param setting アカウント別汎用設定
   */
  private putAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.accountGenericSettingResource.putByEntity(setting);
  }

  /**
   * アカウント別汎用設定を登録します。
   * @param setting アカウント別汎用設定
   */
  private postAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        setting.blUserId = blUserId;
        return this.accountGenericSettingResource.post(setting);
      })
    );
  }

  /**
   * 業務メニュー画面動作設定にparseします
   * @param currentProductCode アカウント別汎用設定
   * @param accountGenericSetting アカウント別汎用設定
   * @returns 業務メニュー画面動作設定
   */
  private parseScreenActionSetting(
    accountGenericSetting?: IAccountGenericSetting
  ): IBizMenuPageScreenActionSetting {
    const baseSetting = _cloneDeep(BaseScreenActionSetting);
    if (accountGenericSetting) {
      // parseできないときはベースを採用
      try {
        return {
          ...baseSetting,
          ...<IBizMenuPageScreenActionSetting>JSON.parse(accountGenericSetting.settingInfoDataJson),
        };
      } catch {
        return baseSetting;
      }
    }

    return baseSetting;
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/biz-menu-page-screen-action-setting-modal/biz-menu-page-screen-action-setting-modal.service.ts